import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { _getFocusedElementPierceShadowDom } from '@angular/cdk/platform';
import { ComponentType } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Component,
  ElementRef,
  TemplateRef
} from '@angular/core';
import { take, tap } from 'rxjs';

import { IrisModalContentType } from '../utilities/modal-content-type.enum';
import { IrisModalContent } from '../utilities/modal-content.type';
import { IrisModalRef } from '../utilities/modal-ref';

@Component({
  template: '',
  standalone: false
})
/**
 * This class handles the modal behavior that is shared by the different types of Iris modal.
 */
export class IrisBaseModalComponent<TContent, TModalInputs, TResponseData> implements AfterViewInit {
  contentTypeEnum = IrisModalContentType;
  contentType!: IrisModalContentType;
  content!: IrisModalContent<TContent>;
  context!: { close: (result?: TResponseData) => void; };

  contentTemplate!: TemplateRef<TContent>;
  contentComponent!: ComponentType<TContent>;

  private elementFocusedBeforeModalWasOpened!: HTMLElement | null;
  private focusTrap!: ConfigurableFocusTrap;

  constructor(
    private focusTrapFactory: ConfigurableFocusTrapFactory,
    private hostElement: ElementRef,
    public ref: IrisModalRef<TContent, TModalInputs, TResponseData>
  ) {}

  /**
   * Called in child component's ngOnInit methods.
   */
  initializeModalContent(): void {
    this.content = this.ref.content;

    if (this.content instanceof TemplateRef) {
      this.contentType = IrisModalContentType.TEMPLATE;
      this.context = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        close: this.ref.close.bind(this.ref)
      };
      this.contentTemplate = this.content;
    } else {
      this.contentType = IrisModalContentType.COMPONENT;
      this.contentComponent = this.content;
    }

    this.ref.afterClosed$().pipe(
      take(1),
      tap(() => this.cleanUpFocus())
    ).subscribe();
  }

  ngAfterViewInit(): void {
    /* Create a focus trap for our modal's main div.
    Note: creating the focus trap via the ConfigurableFocusTrapFactory is preferable to using the cdkTrapFocus
    directive because ConfigurableFocusTraps are implicitly registered and managed by the FocusTrapManager,
    which properly handles toggling focus traps when multiple ones exist simultaneously (like in the case of stacked
    modals). The cdkTrapFocus directive on the other hand,
    simply uses FocusTrap and IS NOT managed by the FocusTrapManager. */
    this.elementFocusedBeforeModalWasOpened = _getFocusedElementPierceShadowDom();
    this.focusTrap = this.focusTrapFactory.create(this.hostElement.nativeElement);
    this.focusTrap.enabled = true;
  }

  private cleanUpFocus(): void {
    if (this.elementFocusedBeforeModalWasOpened) {
      this.elementFocusedBeforeModalWasOpened.focus();
    }

    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
  }
}
